import React, { useEffect, useState } from 'react';
import Loader from '@/components/Loader/Loader';
import { sendAnalytics } from '@/services/analyticsService';
import { shallowEqual, useSelector } from 'react-redux';
import type { RootState } from '@/rootReducer';
import DropdownMenu from '@/components/DropdownMenu/DropdownMenu';
import HorizontalDotsButton from '@/components/HorizontalDotsButton/HorizontalDotsButton';
import {
	createSequenceTemplate,
	deleteSequenceTemplate,
	updateSequenceTemplate
} from '@/services/sequencesService';
import { toast } from 'react-hot-toast';
import Dialog from '@/components/Dialog/Dialog';
import { Checkmark, CloseThin } from '@/resources/assets/images';
import { PlusSignIcon } from '@/components/SvgIcons';
import CroppedText from '@/components/CroppedText/CroppedText';
import Button from '@/components/Button/newButtons';
import { UserRole } from '@/modules/user/userTypes';
import { useTemplates, type TemplatesType } from '../../hooks/useTemplates';
import { useSequencesContext } from '../../SequencesContext';
import AiSequenceDialogForm from '../AiModal/molecules/AiSequenceDialogForm';

export default function NewSequenceContainer() {
	return (
		<div className="-mt-10 flex h-full w-full max-w-[35.125rem] flex-shrink-0 flex-col justify-center gap-2 overflow-hidden lg:mt-0 lg:justify-start">
			<StartFromScratchButton />
			<TemplateSelectList />
		</div>
	);
}

function StartFromScratchButton() {
	const [user] = useSelector(
		(state: RootState) => [state.user.getIn(['authorize', 'user']).toJS()],
		shallowEqual
	);

	const isUserFreeTier =
		user?.role === UserRole.Free || user?.role === UserRole.PreTrial;

	const {
		setSequencePage,
		setSelectedTemplate,
		setSelectedStep,
		selectedSequence,
		setSelectedSequence,
		setAiSequenceModalFields
	} = useSequencesContext();

	const handleGoToStepsCreation = () => {
		setAiSequenceModalFields(undefined);

		const createdStep = {
			id: Math.random().toString(36).substring(2, 9),
			subject: 'New Email',
			body: 'This is the new template',
			sendAfterInterval: 0,
			sendAfterType: 'DAYS',
			order: 0,
			justCreated: true
		};

		setSelectedTemplate({
			id: Math.random().toString(36).substring(2, 9),
			name: 'New Template',
			steps: [createdStep]
		});

		setSelectedStep(createdStep);

		setSelectedSequence({
			...selectedSequence,
			name: selectedSequence?.name || 'New Sequence',
			steps: [createdStep],
			status: selectedSequence?.status || 'DRAFT'
		});

		setSequencePage('STEPS-CREATION');

		sendAnalytics({
			type: 'track',
			userId: user.email,
			eventName: 'user_clicked_start_sequence_from_scratch'
		});
	};

	const resetPreviousAiGeneratedSequence = () => {
		setAiSequenceModalFields(undefined);
		setSelectedTemplate(undefined);
	};

	return isUserFreeTier ? (
		<div className="grid grid-cols-1 items-center gap-4 rounded-2xl lg:border lg:border-dm-grey lg:bg-dm-black lg:px-8 lg:py-6">
			<AiSequenceDialogForm
				trigger={
					<Button
						onClick={resetPreviousAiGeneratedSequence}
						smart
						leftIcon
						className="h-12 w-full"
					>
						Generate with AI
					</Button>
				}
			/>
		</div>
	) : (
		<div className="flex grid-cols-2 flex-col items-center gap-4 rounded-2xl lg:grid lg:border lg:border-dm-grey lg:bg-dm-black lg:px-8 lg:py-6">
			<Button
				className="h-12 w-full"
				leftIcon={<PlusSignIcon size={8} />}
				onClick={handleGoToStepsCreation}
			>
				Start from scratch
			</Button>

			<AiSequenceDialogForm
				trigger={
					<Button
						onClick={resetPreviousAiGeneratedSequence}
						smart
						leftIcon
						className="h-12 w-full"
					>
						Generate with AI
					</Button>
				}
			/>
		</div>
	);
}

function TemplateSelectList() {
	const { templates, getTemplates, isLoading } = useTemplates();

	useEffect(() => {
		getTemplates();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<div className="hidden h-full flex-col overflow-hidden rounded-lg border border-dm-grey bg-dm-black lg:flex">
			<div className="flex h-24 flex-shrink-0 items-center border-b border-dm-grey px-8">
				<span className="text-xl font-semibold">Adopt a template</span>
			</div>
			<div className="flex h-full cursor-pointer flex-col overflow-y-auto overflow-x-hidden border-dm-grey bg-dm-black">
				{isLoading ? (
					<div className="mt-36 flex items-center justify-center">
						<Loader />
					</div>
				) : (
					<>
						{templates?.map(template => (
							<TemplateSelectListItem
								key={template.id}
								template={template}
							/>
						))}
					</>
				)}
			</div>
		</div>
	);
}

function TemplateSelectListItem({ template }: { template: TemplatesType }) {
	const {
		selectedTemplate,
		setSelectedTemplate,
		setSelectedStep,
		selectedSequence,
		setSelectedSequence
	} = useSequencesContext();

	const { getTemplates } = useTemplates();

	const [isDeletingTemplate, setIsDeletingTemplate] = useState(false);
	const [isDeleteLoading, setIsDeleteLoading] = useState(false);
	const [isEditingDescription, setIsEditingDescription] = useState(false);
	const [isEditingName, setIsEditingName] = useState(false);
	const [templateName, setTemplateName] = useState(template.name);
	const [description, setDescription] = useState(template.description);

	function handleSelect() {
		if (!selectedSequence) return;
		setSelectedTemplate(template);
		setSelectedStep(template.steps[0]);
		setSelectedSequence({
			...selectedSequence,
			steps: template.steps
		});
	}

	function handleDuplicateTemplate() {
		createSequenceTemplate({
			name: template.name,
			steps: template.steps,
			description: template.description
		})
			.then(res => {
				if (res.status !== 200) {
					throw new Error(
						'Error duplicating template! Please try again later.'
					);
				}
				toast.success('Template duplicated successfully!');
				getTemplates();
			})
			.catch(err => {
				toast.error(err.message);
			});
	}

	const handleDeleteTemplate = () => {
		if (!template.id) return;
		setIsDeleteLoading(true);
		deleteSequenceTemplate({ templateId: template.id })
			.then(res => {
				if (res.status !== 200) {
					throw new Error(
						'Error deleting template! Please try again later.'
					);
				}
				toast.success('Template deleted successfully!');
				getTemplates();
			})
			.catch(err => {
				toast.error(err.message);
			})
			.finally(() => setIsDeletingTemplate(false));
	};

	function handleTemplateForm(e: React.FormEvent<HTMLFormElement>) {
		e.preventDefault();
		handleUpdateTemplate();
	}

	function handleUpdateTemplate() {
		if (!template.id) return;
		updateSequenceTemplate({
			templateId: template.id,
			name: templateName || template.name || 'New template',
			description: description || ''
		})
			.then(res => {
				setIsEditingDescription(false);
				setIsEditingName(false);

				if (res.status !== 200) {
					throw new Error(
						'Error updating template! Please try again later.'
					);
				}

				toast.success('Template updated successfully!');
				getTemplates();
			})
			.catch(err => {
				toast.error(err.message);
			});
	}

	const handleDeleteOpen = () => {
		setIsDeletingTemplate(!isDeletingTemplate);
	};

	function handleCancelEdit() {
		setIsEditingDescription(false);
		setDescription(template.description);

		setIsEditingName(false);
		setTemplateName(template.name);
	}

	const dropdownTemplateOptions = [
		{ id: '1', label: 'Duplicate', onClick: handleDuplicateTemplate },
		...(!template.default
			? [
					{
						id: '2',
						label: 'Edit name',
						onClick: () => setIsEditingName(true)
					},
					{
						id: '3',
						label: 'Edit description',
						onClick: () => setIsEditingDescription(true)
					},
					{
						id: '6',
						label: 'Remove',
						onClick: () => setIsDeletingTemplate(true)
					}
				]
			: [])
	];

	const isSelected = selectedTemplate?.id === template.id;

	return (
		<>
			<Dialog isOpen={isDeletingTemplate} handleOpen={handleDeleteOpen}>
				<span className="mb-2 text-2xl font-medium text-dm-red">
					Confirm delete
				</span>
				<span className="text-lg text-gray-200">
					Are you sure you want to remove the selected template?
				</span>
				<div className="flex justify-between gap-10">
					<Button variant="text" onClick={handleDeleteOpen}>
						Cancel
					</Button>
					<Button
						variant="secondary"
						red
						onClick={handleDeleteTemplate}
					>
						{isDeleteLoading ? 'Deleting...' : 'Confirm Delete'}
					</Button>
				</div>
			</Dialog>
			<div
				role="button"
				tabIndex={0}
				onClick={handleSelect}
				onKeyDown={e => e.key === 'Tab' && handleSelect()}
				className={`flex h-24 flex-shrink-0 border-b pr-8 ${isSelected ? 'border-dm-gold bg-dm-black3' : 'border-dm-grey'} `}
			>
				<div className="flex w-[12.25rem] flex-shrink-0 flex-col justify-center gap-1 overflow-hidden px-8">
					{isEditingName ? (
						<form
							onSubmit={handleTemplateForm}
							className="flex h-full items-center gap-1"
						>
							<input
								value={templateName}
								onChange={e => setTemplateName(e.target.value)}
								style={{
									width: '85%'
								}}
								className="border bg-gray-200 text-black"
								name="edit-name"
								id="edit-name"
							/>
							<div className="flex flex-col items-center gap-3 text-white">
								<button
									type="submit"
									id="campaigns-rename-checkmark"
								>
									<Checkmark width={17} />
								</button>
								<button
									onClick={handleCancelEdit}
									type="button"
								>
									<CloseThin width={12} />
								</button>
							</div>
						</form>
					) : (
						<div className="text-lg text-gray-200">
							<CroppedText
								text={template.name || ''}
								maxLength={35}
							/>
						</div>
					)}
					<div className="text-sm text-dm-mid-grey">
						{template.steps.length} Steps
					</div>
				</div>
				<div className="w-[17rem] flex-shrink-0 overflow-y-auto border-l border-dashed border-dm-grey px-4 py-3 text-xs font-semibold text-dm-mid-grey">
					{isEditingDescription ? (
						<form onSubmit={handleTemplateForm} className="h-full">
							<textarea
								value={description}
								onChange={e => setDescription(e.target.value)}
								style={{
									width: '90%'
								}}
								className="ml-4 h-full border bg-gray-200 px-1 py-0 text-xs text-black"
								name="edit-description"
								id="edit-description"
							/>
						</form>
					) : (
						<p className="px-4 text-xs">{template.description}</p>
					)}
				</div>
				<div className="relative flex w-full items-center">
					{isEditingDescription ? (
						<div className="flex h-full flex-col items-center justify-center gap-3 text-white">
							<button
								type="button"
								id="campaigns-rename-checkmark"
								onClick={handleUpdateTemplate}
							>
								<Checkmark width={20} />
							</button>
							<button onClick={handleCancelEdit} type="button">
								<CloseThin width={14} />
							</button>
						</div>
					) : (
						<div>
							<DropdownMenu
								itemClassName="text-white py-3 px-2 hover:bg-dm-black3"
								options={dropdownTemplateOptions}
							>
								<HorizontalDotsButton />
							</DropdownMenu>
						</div>
					)}
				</div>
			</div>
		</>
	);
}
